import React, { useState, useEffect } from "react";
import PropTypes from "prop-types";
import styles from "./SearchBar.module.css";
import SearchIcon from "../assets/Search_Icon.svg";
import { ClearIcon } from "../assets/ClearIcon";

/**
 * An interactive Search Bar component with a customizable placeholder text.
 * Logs inputs if minimum character prop is satisfied with an optional delay.
 * Width of Search Bar determined by container width.
 * Includes a clear button to reset input.
 */

export function SearchBar({
  placeholderText,
  onInput,
  searchDelay,
  minChar,
  value,
  noResults,
}) {
  const isControlled = typeof value === "string";
  const [internalValue, setInternalValue] = useState("");

  const input = isControlled ? value : internalValue;

  useEffect(() => {
    if (isControlled) {
      return undefined;
    }

    if (input.length >= minChar) {
      const timer = setTimeout(() => {
        onInput(input);
      }, searchDelay);

      return () => clearTimeout(timer);
    }
  }, [isControlled, input, onInput, searchDelay, minChar]);

  const handleClear = () => {
    if (isControlled) {
      onInput("");
    } else {
      setInternalValue("");
    }
  };

  return (
    <>
      <div className={styles.wrapper}>
        <div className={styles.container}>
        <label htmlFor="search-bar" className={styles.searchLabel} hidden>
          Search
        </label>
        <img src={SearchIcon} alt="" className={styles.searchIcon} />
        <input
          type="text"
          id="search-bar"
          placeholder={placeholderText}
          className={styles.searchBar}
          value={input}
          onChange={(key) => {
            if (!isControlled) {
              setInternalValue(key.target.value);
            } else {
              onInput(key.target.value);
            }
          }}
        ></input>
        {input && (
          <button
            type="button"
            className={styles.clearButton}
            onClick={handleClear}
            aria-label="Clear search"
          >
            <ClearIcon width={12} height={12} />
          </button>
        )}
        </div>
        {noResults ? (
          <div className={styles.noResults} role="status">
            No results found
          </div>
        ) : null}
      </div>
    </>
  );
}


SearchBar.propTypes = {
  // placeholder text
  placeholderText: PropTypes.string,

  // function that fires when user types and minChar/searchDelay is satisfied
  onInput: PropTypes.func,

  // number of ms before onInput is called
  searchDelay: PropTypes.number,

  // minimum characters needed to fire onInput
  minChar: PropTypes.number,

  // controlled input value
  value: PropTypes.string,

  // flag to indicate no results
  noResults: PropTypes.bool,
};

SearchBar.defaultProps = {
  // default delay of 0
  searchDelay: 0,

  // default minChar of 0
  minChar: 0,

  value: undefined,
  noResults: false,
};
